/**
 * Resolved Signer DTOs (Issue #1305)
 *
 * A `ResolvedSigner` is the concrete output of the party resolver — a named
 * recipient with email/name and cross-service identity FKs ready to be
 * materialised into a `SigningRecipient` row.
 *
 * Two shapes live here:
 * - `SignerInputSchema` — what callers can POST to `sendForEsign` when they
 *   want to override auto-resolution. The API Gateway validates inbound tRPC
 *   payloads against this.
 * - `ResolvedSignerSchema` — the shape the party resolver returns (and that
 *   the `resolveSigners` tRPC query returns to the UI).
 *
 * The two schemas are structurally compatible: every `ResolvedSigner` is a
 * valid `SignerInput`. The extra `source` field on `ResolvedSigner` lets the
 * UI distinguish "we filled this in for you" from "you need to fill this in".
 */

import { z } from 'zod';
import { SignerRoleSchema } from '../enums/signer-role';

/**
 * Origin of a resolved signer. `auto` means the resolver pulled it from loan
 * data; `manual` means the policy expected a signer the resolver can't fill
 * (witness / notary / CC, or a missing authorised officer on an LLC borrower)
 * and the UI must prompt the user.
 */
export const SignerSourceSchema = z.enum(['auto', 'manual']);

export type SignerSource = z.infer<typeof SignerSourceSchema>;

/**
 * Signer input accepted by `sendForEsign`. When `signers` is omitted the
 * backend auto-resolves from the template's `signerPolicy`.
 *
 * Invariants (enforced by superRefine):
 * - Lender-side roles (`lender_signer`, `credit_manager`) MUST have `userId`.
 * - Consumer-side roles (`borrower`, `co_borrower`, `guarantor`,
 *   `authorized_officer`) should have `consumerId` but it's not strictly
 *   required at the schema layer — the service will look it up from email
 *   if not supplied (legacy paths).
 */
export const SignerInputSchema = z
  .object({
    role: SignerRoleSchema,
    email: z.string().email(),
    name: z.string().min(1),
    phone: z.string().optional(),
    /** 1-based signing order (DocuSeal processes in ascending order). */
    signingOrder: z.number().int().min(1).max(20),
    /** Consumer record FK for consumer-side signers. */
    consumerId: z.string().uuid().optional(),
    /** User record FK for lender-side signers — required when role is lender_signer/credit_manager. */
    userId: z.string().uuid().optional(),
    /** Counter-sign slot (lender signs after all consumer signers complete). */
    counterSign: z.boolean().default(false),
  })
  .superRefine((item, ctx) => {
    if ((item.role === 'lender_signer' || item.role === 'credit_manager') && !item.userId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['userId'],
        message: `Signer with role "${item.role}" must declare userId (lender-side signers require a platform User).`,
      });
    }
  });

export type SignerInput = z.infer<typeof SignerInputSchema>;

/**
 * A signer resolved from loan data (Case / BorrowingEntity / LoanGuarantor /
 * EntityOwnership / User). The `source` field flags whether the resolver
 * filled this in (`auto`) or left it as a stub for the UI to complete
 * (`manual` — policy asked for a role the resolver can't populate).
 */
export const ResolvedSignerSchema = z
  .object({
    role: SignerRoleSchema,
    email: z.string().email().or(z.literal('')),
    name: z.string(),
    phone: z.string().optional(),
    signingOrder: z.number().int().min(1).max(20),
    consumerId: z.string().uuid().optional(),
    userId: z.string().uuid().optional(),
    counterSign: z.boolean(),
    source: SignerSourceSchema,
  })
  .superRefine((item, ctx) => {
    // 'auto' signers must have a real email so DocuSeal can reach them.
    // 'manual' stubs may legitimately have empty email (the UI collects it).
    if (item.source === 'auto' && !item.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: `Auto-resolved signer for role "${item.role}" is missing an email address.`,
      });
    }
  });

export type ResolvedSigner = z.infer<typeof ResolvedSignerSchema>;

/**
 * Array of resolved signers — the output shape of `PartyResolverService.resolve()`
 * and the `resolveSigners` tRPC query.
 */
export const ResolvedSignerListSchema = z.array(ResolvedSignerSchema);
